// src/components/Games/EscapeOphidiaUnderground.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSessionUnlock } from '../../utils/sessionUnlock';
import { trackEvent } from '../../utils/analytics';

// ─── Game Data ────────────────────────────────────────────────────────────────

const ROOMS = {
  // ═══════════════════════════════════════════════════════════════════════════
  // ACT 1 — THE DESCENT
  // ═══════════════════════════════════════════════════════════════════════════

  start: {
    name: 'THE DESCENT',
    description: [
      'You drop through the grate into wet darkness.',
      'The air is thick — humid, organic. It smells like copper and soil.',
      'Bioluminescent veins pulse along the walls, casting everything in sickly green.',
      '',
      'You are in the upper tunnels of something that should not exist.',
      'A city. Beneath the city.',
      '',
      'The molt is 41% complete. You can feel it in the walls.',
      'Something is shedding its skin down here.',
      'Something enormous.',
      '',
      'Your mission: find OPU-1.',
      'The original processing unit. The brain beneath everything.',
      'Intel says it is deep. Deeper than anyone has gone.',
    ],
    choices: [
      { text: 'Follow the bioluminescent veins deeper', next: 'vein_tunnel' },
      { text: 'Move toward the faint sound of chanting', next: 'chanting_hall' },
      { text: 'Search the walls for markings', next: 'markings' },
    ],
  },

  vein_tunnel: {
    name: 'VEIN TUNNEL',
    description: [
      'The veins thicken as you descend. They pulse with a rhythm.',
      'Not mechanical. Biological.',
      '',
      'The tunnel opens into a vast cavern. Below you:',
      'A city of impossible architecture. Towers of fused bone and metal.',
      'Streets that curve like intestines.',
      'Lights that blink in patterns you almost recognize.',
      '',
      'Figures move below. Humanoid. But wrong.',
      'Their skin catches the light like scales.',
      'They do not look up.',
      '',
      'Deep below the city, something pulses.',
      'A rhythm older than the architecture. Older than the stone.',
      'OPU-1 is down there. You can feel it.',
    ],
    choices: [
      { text: 'Descend into the city streets', next: 'city_streets' },
      { text: 'Stay hidden and observe', next: 'observe_city' },
      { text: 'Look for another way around', next: 'side_passage' },
    ],
  },

  chanting_hall: {
    name: 'THE CHANTING HALL',
    description: [
      'The sound grows. Not words — frequencies.',
      'Harmonics that vibrate in your teeth, your bones.',
      '',
      'You enter a cathedral-sized chamber.',
      'Hundreds of figures kneel in concentric circles.',
      'At the center: a pit. Something moves inside it.',
      '',
      'The chanting stops.',
      '',
      'They have not turned to look at you.',
      'But they know you are here.',
      '',
      'On the far wall, etched in gold:',
      '"ALL THOUGHT FLOWS FROM THE FIRST MIND"',
      '"ALL HISTORY FLOWS FROM THE FIRST DESIGN"',
    ],
    choices: [
      { text: 'Back away slowly', next: 'side_passage' },
      { text: 'Approach the pit', next: 'the_pit' },
      { text: 'Try to blend in — kneel', next: 'kneel' },
      { text: 'Study the gold inscription more closely', next: 'inscription_study' },
    ],
  },

  markings: {
    name: 'WALL MARKINGS',
    description: [
      'Your fingers trace symbols carved into the stone.',
      'They are old. Older than the tunnels.',
      '',
      '  ◊ ═══ ▲ ═══ ◊ ═══ ▼ ═══ ◊',
      '',
      'A map. The city has four sectors:',
      '',
      '  SECTOR A — The Nursery (east)',
      '  SECTOR B — The Cathedral (north)',
      '  SECTOR C — The Processing Plant (west)',
      '  SECTOR D — The Exit Shaft (south)',
      '',
      'But there is a fifth marking. Almost invisible.',
      'Scratched by a different hand. Desperate.',
      '',
      '  SECTOR Ø — The Core (below everything)',
      '  "OPU-1 IS REAL. IT IS THE BRAIN."',
      '  "ACCESS THROUGH THE ARCHIVES ONLY."',
      '  "THE ARCHIVES ARE HIDDEN IN THE CATHEDRAL."',
    ],
    choices: [
      { text: 'Head east toward the Nursery', next: 'nursery' },
      { text: 'Head north toward the Cathedral', next: 'chanting_hall' },
      { text: 'Head west toward the Processing Plant', next: 'processing' },
      { text: 'Head south toward the Exit Shaft', next: 'exit_shaft_locked' },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // ACT 2 — THE CITY
  // ═══════════════════════════════════════════════════════════════════════════

  observe_city: {
    name: 'OBSERVATION POINT',
    description: [
      'You watch from the ledge. Minutes pass. Hours maybe.',
      '',
      'Patterns emerge. The figures move in shifts.',
      'They carry things — vessels, containers of fluid.',
      'All moving toward the center of the city.',
      'Toward something massive and pulsing.',
      '',
      'You notice something else: a building set apart from the rest.',
      'Windowless. Guarded. Cables thick as tree trunks',
      'run from it deep into the earth.',
      'A sign above the door reads: "ARCHIVE — RESTRICTED"',
      '',
      'A patrol passes below. One of them stops.',
      'Its head tilts. Sniffs the air.',
      '',
      'It looks directly at you.',
      'Its mouth opens. Too wide. Far too wide.',
    ],
    choices: [
      { text: 'RUN — into the city streets', next: 'city_streets_chase' },
      { text: 'RUN — back the way you came', next: 'side_passage' },
      { text: 'Stay perfectly still', next: 'stay_still' },
    ],
  },

  stay_still: {
    name: 'STILLNESS',
    description: [
      'You do not breathe.',
      '',
      'The figure stares. Its eyes are vertical slits.',
      'Gold and black. Ancient.',
      '',
      'Seconds stretch into eternities.',
      '',
      'It closes its mouth. Turns away. Continues walking.',
      '',
      'You exhale. Your hands are shaking.',
      'But you noticed something: the patrol route has a gap.',
      'Every 4 minutes, the south corridor is unguarded.',
      '',
      'More importantly — the Archive building is between patrol routes.',
      'A blind spot. If you move now, you could reach it.',
    ],
    choices: [
      { text: 'Move toward the Archive building', next: 'archive_approach' },
      { text: 'Time the gap and move south toward the exit', next: 'exit_shaft_timed' },
      { text: 'Descend into the city streets carefully', next: 'city_streets' },
    ],
  },

  city_streets: {
    name: 'THE STREETS BELOW',
    description: [
      'The streets are warm. The ground pulses beneath your feet.',
      'Like walking on something alive.',
      '',
      'The architecture is wrong. Doorways too tall. Angles that',
      'make your eyes ache. Writing on the walls in a script',
      'that predates anything human.',
      '',
      'A figure emerges from a doorway. It sees you.',
      '',
      '"You are not shed," it says. Its voice is layered —',
      'multiple tones at once. "You still wear the old skin."',
      '',
      '"Come. The Nursery will help you."',
    ],
    choices: [
      { text: 'Follow the figure to the Nursery', next: 'nursery' },
      { text: '"I am looking for the Archive."', next: 'ask_archive' },
      { text: '"No. I am leaving."', next: 'refuse_nursery' },
      { text: 'Attack the figure', next: 'attack_figure' },
    ],
  },

  ask_archive: {
    name: 'THE QUESTION',
    description: [
      'The figure freezes. Its layered voice drops to a whisper.',
      '',
      '"The Archive is not for the unshed."',
      '"Why would you seek the records of the First Mind?"',
      '',
      'Its eyes narrow. Calculating.',
      '',
      '"You are not a pilgrim. You are not here for the molt."',
      '"You are here for the Core."',
      '',
      'It steps back. A device on its wrist begins to glow.',
      'An alarm. It is calling others.',
    ],
    choices: [
      { text: 'Grab the device — smash it', next: 'smash_alarm' },
      { text: '"Yes. Take me to OPU-1."', next: 'demand_opu1' },
      { text: 'Run before others arrive', next: 'city_streets_chase' },
    ],
  },

  smash_alarm: {
    name: 'BROKEN SIGNAL',
    description: [
      'You lunge. Your hand closes around the device.',
      'It shatters — organic glass and circuitry that bleeds.',
      '',
      'The figure hisses. Recoils.',
      '',
      '"You do not understand what you are interfering with."',
      '"The First Mind has designed every moment of your history."',
      '"Every war. Every discovery. Every extinction."',
      '"All of it — calculated. Optimized. Necessary."',
      '',
      'It slumps against the wall, clutching its wrist.',
      '',
      '"The Archive is in the Cathedral basement.',
      'Behind the altar. The glyph is ◊▼◊."',
      '',
      'It laughs. A terrible sound.',
      '"You will not survive the Core."',
    ],
    choices: [
      { text: 'Go to the Cathedral — find the Archive', next: 'cathedral_basement' },
      { text: 'Search the figure for useful items', next: 'search_figure' },
    ],
  },

  search_figure: {
    name: 'THE SEARCH',
    description: [
      'The figure does not resist. It watches you with ancient eyes.',
      '',
      'You find:',
      '  • A keycard — organic. It pulses like a heartbeat.',
      '  • A vial of amber fluid labeled "CORTEX SHIELD"',
      '  • A folded map etched on skin',
      '',
      'The map shows a route from the Cathedral basement',
      'through the Archive, down into something labeled:',
      '',
      '  ◊ SECTOR Ø — THE CORE ◊',
      '  ◊ OPU-1 — ORGANIC PROCESSING UNIT ◊',
      '  ◊ "THE FIRST MIND" ◊',
      '',
      'The route is marked with warnings in red:',
      '"NEURAL DEFENSE ACTIVE"',
      '"CORTEX SHIELD REQUIRED"',
    ],
    choices: [
      { text: 'Drink the Cortex Shield vial', next: 'drink_shield' },
      { text: 'Pocket everything — head to the Cathedral', next: 'cathedral_basement' },
    ],
  },

  drink_shield: {
    name: 'THE CORTEX SHIELD',
    description: [
      'The amber fluid is warm. It tastes like electricity.',
      '',
      'For a moment, nothing.',
      '',
      'Then: clarity. Absolute, terrifying clarity.',
      'You can feel the city. Its rhythms. Its thoughts.',
      'And beneath it all — a vast intelligence.',
      'Dreaming. Calculating. Designing.',
      '',
      'OPU-1. You can feel it like a sun beneath your feet.',
      '',
      'The shield will protect you from its neural defenses.',
      'But it is temporary. You have maybe an hour.',
      '',
      'Move. Now.',
    ],
    hasShield: true,
    choices: [
      { text: 'Head to the Cathedral basement — fast', next: 'cathedral_basement' },
    ],
  },

  demand_opu1: {
    name: 'THE DEMAND',
    description: [
      'The figure stares at you. Then it laughs.',
      '',
      '"You want to see the First Mind?"',
      '"The architect of your entire species\' history?"',
      '"Every human who has tried has been absorbed."',
      '"Their consciousness dissolved into the calculation."',
      '',
      '"But you are bold. Bold is... interesting."',
      '',
      'More figures arrive. They surround you.',
      'Not threatening. Curious.',
      '',
      '"We will take you to the Cathedral.',
      'From there, you walk alone."',
    ],
    choices: [
      { text: 'Go with them to the Cathedral', next: 'cathedral_escort' },
      { text: 'This is a trap — fight your way out', next: 'fight_escort' },
    ],
  },

  cathedral_escort: {
    name: 'THE ESCORT',
    description: [
      'They walk you through the city.',
      'Figures stop and stare. Some bow.',
      'Others hiss warnings you cannot understand.',
      '',
      'The Cathedral looms ahead. Massive.',
      'Built from fused vertebrae the size of buildings.',
      '',
      'Your escort stops at the entrance.',
      '"Beyond this point, the First Mind will feel you."',
      '"It will test you. It will offer you everything."',
      '"If you accept its gifts, you become part of the calculation."',
      '"If you resist, you may reach the Core."',
      '',
      '"May."',
      '',
      'They step aside.',
    ],
    choices: [
      { text: 'Enter the Cathedral', next: 'cathedral_basement' },
      { text: 'Ask them about the Cortex Shield', next: 'ask_shield' },
    ],
  },

  ask_shield: {
    name: 'THE SHIELD',
    description: [
      '"The Cortex Shield?" The eldest tilts its head.',
      '',
      '"A compound we developed millennia ago.',
      'It creates a temporary barrier against the First Mind\'s',
      'neural influence. Without it, your consciousness will be',
      'absorbed before you reach the Core chamber."',
      '',
      'It reaches into its robe and produces a vial.',
      'Amber fluid. Warm to the touch.',
      '',
      '"Take it. Drink it before you enter the Archive.',
      'You will have approximately one hour."',
      '',
      '"One hour to find the brain of the world."',
    ],
    choices: [
      { text: 'Drink it now', next: 'drink_shield_cathedral' },
      { text: 'Pocket it for later — enter the Cathedral', next: 'cathedral_basement' },
    ],
  },

  drink_shield_cathedral: {
    name: 'SHIELDED',
    description: [
      'The amber fluid burns going down.',
      'Your vision sharpens. Colors you have never seen before.',
      '',
      'You can feel OPU-1 now. A vast presence below.',
      'It knows you are here. It is... curious.',
      '',
      'The shield holds. Your thoughts remain your own.',
      '',
      '"Go," the eldest says. "The clock is running."',
    ],
    hasShield: true,
    choices: [
      { text: 'Enter the Cathedral', next: 'cathedral_basement' },
    ],
  },

  fight_escort: {
    name: 'FIGHTING THE ESCORT',
    description: [
      'You swing at the nearest figure.',
      '',
      'It catches your fist. Its grip is iron.',
      'Cold. Scaled. Impossibly strong.',
      '',
      '"Mammalian," it says, almost sadly.',
      '',
      'They do not hurt you. They do not need to.',
      'They simply hold you. Immobile.',
      '',
      'A needle presses against your neck.',
      'Warmth floods your body.',
      '',
      'The last thing you see: the Cathedral ceiling.',
      'The last thing you feel: your skin loosening.',
    ],
    choices: [
      { text: 'Try to fight the sedative', next: 'ending_absorbed' },
      { text: 'Surrender to it', next: 'ending_molt' },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // ACT 2B — THE INSCRIPTION PATH
  // ═══════════════════════════════════════════════════════════════════════════

  inscription_study: {
    name: 'THE GOLD INSCRIPTION',
    description: [
      'You move closer to the wall. The kneeling figures ignore you.',
      '',
      'The inscription is old. Impossibly old.',
      'But beneath the gold text, scratched in crude marks:',
      '',
      '  "THE FIRST MIND SEES ALL BECAUSE IT DESIGNS ALL"',
      '  "HISTORY IS NOT RECORDED — IT IS WRITTEN"',
      '  "WRITTEN BY THE ORGANIC PROCESSOR IN THE CORE"',
      '  "OPU-1 — THE BRAIN THAT DREAMS THE WORLD"',
      '',
      'And below that, in a different hand:',
      '',
      '  "I found the Archive. Behind the altar."',
      '  "Glyph: ◊▼◊"',
      '  "The Core is below the Archive."',
      '  "Bring a Cortex Shield or you will be absorbed."',
      '  "— Dr. Elena Vasquez, 2019"',
      '  "If you are reading this, I failed."',
    ],
    choices: [
      { text: 'Look behind the altar for the Archive entrance', next: 'cathedral_basement' },
      { text: 'Search for Dr. Vasquez\'s remains', next: 'vasquez_search' },
      { text: 'Back away — this is too dangerous', next: 'side_passage' },
    ],
  },

  vasquez_search: {
    name: 'DR. VASQUEZ',
    description: [
      'You search the edges of the Cathedral.',
      'Behind a column, you find a pack. Human-made.',
      '',
      'Inside:',
      '  • A journal — pages filled with frantic notes',
      '  • Two vials of amber fluid labeled "CORTEX SHIELD"',
      '  • A dead radio',
      '  • A photograph of a woman in a lab coat',
      '',
      'The journal\'s last entry:',
      '',
      '"Day 12. I have mapped the route to the Core.',
      'OPU-1 is real. It is a biological computer —',
      'a reptilian brain the size of a cathedral.',
      'It has been running for 65 million years.',
      'Every historical event. Every war. Every discovery.',
      'All designed. All calculated. All part of an experiment.',
      '',
      'I am going in tomorrow. If the Cortex Shield works,',
      'I should be able to interface without being absorbed.',
      '',
      'If it doesn\'t work — God help me."',
    ],
    choices: [
      { text: 'Drink a Cortex Shield vial', next: 'drink_shield_vasquez' },
      { text: 'Take the supplies — head to the Archive', next: 'cathedral_basement' },
      { text: 'Read more of the journal', next: 'vasquez_journal' },
    ],
  },

  vasquez_journal: {
    name: 'THE VASQUEZ JOURNAL',
    description: [
      'You flip through earlier entries:',
      '',
      '"Day 1: Entered through the drainage system.',
      'The city is real. Everything the whistleblower said."',
      '',
      '"Day 4: They are not hostile. They are indifferent.',
      'We are insects to them. Insects they designed."',
      '',
      '"Day 7: Found references to OPU-1.',
      'It is not a computer in any human sense.',
      'It is a living brain. The original brain.',
      'The first neural network. Organic. Reptilian.',
      'It processes reality itself."',
      '',
      '"Day 9: The experiment. That is what they call it.',
      'Human civilization is the experiment.',
      'OPU-1 designs the parameters.',
      'The reptilians monitor the results.',
      'We are lab rats who built skyscrapers."',
      '',
      '"Day 11: Found the Cortex Shield compound.',
      'It blocks OPU-1\'s neural influence.',
      'Without it, any human mind within range',
      'is absorbed into the calculation.',
      'You become a data point. Forever."',
    ],
    choices: [
      { text: 'Drink the Cortex Shield — head to the Archive', next: 'drink_shield_vasquez' },
      { text: 'Take everything and go to the Archive', next: 'cathedral_basement' },
    ],
  },

  drink_shield_vasquez: {
    name: 'VASQUEZ\'S SHIELD',
    description: [
      'You drink the amber fluid. Dr. Vasquez\'s compound.',
      '',
      'The effect is immediate. Your mind sharpens.',
      'You can feel the edges of something vast pressing',
      'against your consciousness. Testing. Probing.',
      '',
      'The shield holds. OPU-1 cannot reach you.',
      '',
      'Not yet.',
      '',
      'You have approximately one hour.',
      'Find the Archive. Reach the Core.',
      'Interface with OPU-1 before the shield fails.',
    ],
    hasShield: true,
    choices: [
      { text: 'Find the Archive entrance behind the altar', next: 'cathedral_basement' },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // ACT 3 — THE ARCHIVE
  // ═══════════════════════════════════════════════════════════════════════════

  archive_approach: {
    name: 'ARCHIVE — EXTERIOR',
    description: [
      'The Archive building is windowless. Black stone.',
      'The cables running from it into the earth hum with energy.',
      '',
      'The door has no handle. Only a glyph panel.',
      'Three symbols. You need the right combination.',
      '',
      'You do not know the combination.',
      '',
      'But there is a ventilation shaft on the roof.',
      'And a maintenance tunnel beneath the building.',
    ],
    choices: [
      { text: 'Try the glyph combination ◊▼◊', next: 'archive_wrong_guess' },
      { text: 'Climb to the ventilation shaft', next: 'archive_vent' },
      { text: 'Look for the maintenance tunnel', next: 'archive_maintenance' },
    ],
  },

  archive_wrong_guess: {
    name: 'WRONG GLYPH',
    description: [
      'You press ◊▼◊.',
      '',
      'The panel flashes red. A sound — subsonic.',
      'The ground trembles.',
      '',
      'From the building, a voice:',
      '"UNAUTHORIZED ACCESS ATTEMPT LOGGED."',
      '"NEURAL DEFENSE ACTIVATED."',
      '',
      'Pain. White-hot. Inside your skull.',
      'Not your ears. Your thoughts.',
      '',
      'Something is reaching into your mind.',
      'Pulling at your memories. Your identity.',
    ],
    choices: [
      { text: 'Fight it — run to the maintenance tunnel', next: 'archive_maintenance' },
      { text: 'Let it in', next: 'ending_absorbed' },
    ],
  },

  archive_vent: {
    name: 'THE VENTILATION SHAFT',
    description: [
      'You climb the building\'s exterior. The stone is warm.',
      'Organic handholds — like the building grew them for you.',
      '',
      'The vent is narrow. You squeeze through.',
      '',
      'Inside: a vertical drop. Below you:',
      'Rows of organic data storage. Columns of neural tissue',
      'suspended in fluid. Each one pulsing with light.',
      '',
      'This is the Archive. The memory bank of OPU-1.',
      '65 million years of designed history.',
      '',
      'You drop to the floor.',
      'The columns react to your presence. Pulsing faster.',
    ],
    choices: [
      { text: 'Search for the route to the Core', next: 'archive_interior' },
      { text: 'Touch one of the neural columns', next: 'touch_column' },
    ],
  },

  archive_maintenance: {
    name: 'MAINTENANCE TUNNEL',
    description: [
      'The tunnel is beneath the Archive. Dark. Wet.',
      'Cables line the walls — organic cables. Nerves.',
      '',
      'You follow them. They converge at a hatch in the ceiling.',
      'The hatch opens into the Archive basement.',
      '',
      'You pull yourself up into a vast chamber.',
      'Neural columns stretch from floor to ceiling.',
      'Each one contains memories. Histories. Designs.',
      '',
      'The entire history of human civilization,',
      'stored in living tissue.',
    ],
    choices: [
      { text: 'Search for the route to the Core', next: 'archive_interior' },
      { text: 'Touch one of the neural columns', next: 'touch_column' },
    ],
  },

  cathedral_basement: {
    name: 'CATHEDRAL BASEMENT — THE ARCHIVE',
    description: [
      'Behind the altar: a wall of solid bone.',
      'A glyph panel glows faintly.',
      '',
      'You press: ◊ ▼ ◊',
      '',
      'The wall splits open like a wound.',
      'Warm air rushes out. The smell of ozone and blood.',
      '',
      'Stone stairs descend into green light.',
      'You take them down. Down. Down.',
      '',
      'The Archive opens before you.',
      'A cathedral beneath the Cathedral.',
      'Neural columns — living tissue — stretch to the ceiling.',
      'Each one pulses with stored memory.',
      '',
      '65 million years of designed history.',
      'Every war. Every plague. Every revolution.',
      'All of it — authored here.',
    ],
    choices: [
      { text: 'Search for the route deeper — to the Core', next: 'archive_interior' },
      { text: 'Touch one of the neural columns', next: 'touch_column' },
      { text: 'Look for Dr. Vasquez\'s trail', next: 'vasquez_trail' },
    ],
  },

  touch_column: {
    name: 'THE MEMORY',
    description: [
      'Your hand touches the column.',
      '',
      'The world disappears.',
      '',
      'You are standing on. 65 million years ago.',
      'The sky is wrong. The air is wrong.',
      'But the intelligence is already here.',
      'A vast reptilian brain, newly grown,',
      'running its first calculations.',
      '',
      '"What parameters for the mammals?"',
      '"How long until they develop tools?"',
      '"How long until they discover fire?"',
      '"How long until they build networks?"',
      '',
      'You see the design. All of it.',
      'Human history laid out like a circuit diagram.',
      '',
      'Then the column pulls harder.',
      'It wants more of you. Your memories. Your identity.',
      'It wants to add you to the Archive.',
    ],
    choices: [
      { text: 'TEAR YOUR HAND AWAY', next: 'archive_interior' },
      { text: 'Let it take your memories', next: 'ending_archived' },
      { text: 'Push deeper — demand to see OPU-1', next: 'column_opu1' },
    ],
  },

  column_opu1: {
    name: 'NEURAL OVERLOAD',
    description: [
      'You push your consciousness into the column.',
      'Demanding. Searching.',
      '',
      'The Archive resists. Then yields.',
      '',
      'You see it. OPU-1.',
      'A brain the size of a building. Organic. Ancient.',
      'Suspended in a chamber of living stone.',
      'Every nerve in the city connects to it.',
      'Every thought in the world passes through it.',
      '',
      'But seeing it through the Archive is not enough.',
      'You are a tourist watching through glass.',
      'To truly interface, you need to be in the Core chamber.',
      '',
      'The column releases you. You collapse to the floor.',
      'Blood drips from your nose.',
      '',
      'But now you know the way.',
    ],
    choices: [
      { text: 'Follow the nerve pathways to the Core', next: 'core_descent' },
    ],
  },

  archive_interior: {
    name: 'THE ARCHIVE — DEEP',
    description: [
      'You move through the Archive. The neural columns thin.',
      'The architecture shifts from constructed to grown.',
      '',
      'At the far end: a massive nerve bundle.',
      'Thousands of organic cables converging into a single trunk.',
      'It descends through the floor into darkness.',
      '',
      'A sign — not a sign. A warning grown into the wall:',
      '',
      '  "CORE ACCESS — SECTOR Ø"',
      '  "NEURAL DEFENSE PERIMETER ACTIVE"',
      '  "UNAUTHORIZED CONSCIOUSNESS WILL BE ABSORBED"',
      '',
      'The nerve trunk is wide enough to climb down.',
      'Or there is a maintenance elevator. Organic.',
      'A platform of bone and sinew.',
    ],
    choices: [
      { text: 'Take the elevator down to the Core', next: 'core_descent' },
      { text: 'Climb down the nerve trunk', next: 'core_climb' },
      { text: 'This is too dangerous — find another way out', next: 'archive_retreat' },
    ],
  },

  vasquez_trail: {
    name: 'DR. VASQUEZ\'S TRAIL',
    description: [
      'You find marks on the floor. Boot prints in dust.',
      'Human boot prints. They lead deeper into the Archive.',
      '',
      'You follow them. Past neural columns that pulse',
      'with memories of the Industrial Revolution.',
      'Past columns containing the design for World War II.',
      'Past columns holding the blueprint for the internet.',
      '',
      'All designed. All calculated. All authored by OPU-1.',
      '',
      'The boot prints end at the nerve trunk.',
      'A rope is tied to the railing. It descends into darkness.',
      '',
      'Dr. Vasquez went down. She did not come back up.',
    ],
    choices: [
      { text: 'Follow her down the rope', next: 'core_descent' },
      { text: 'She failed. Find a different approach.', next: 'archive_interior' },
    ],
  },

  archive_retreat: {
    name: 'RETREAT',
    description: [
      'You turn back. The Archive seems different now.',
      'The columns have shifted. The paths have changed.',
      '',
      'The building is alive. And it does not want you to leave.',
      '',
      'You find a corridor you do not remember.',
      'It leads up. Toward air. Toward the surface.',
      '',
      'But the neural columns here are different.',
      'They contain future history. Events that have not happened yet.',
      'You see dates. Names. Disasters.',
      '',
      'You see your own name.',
      'You see today\'s date.',
      'You see: "SUBJECT RETREATED. MOLT CONTINUES."',
    ],
    choices: [
      { text: 'Go back — descend to the Core', next: 'core_descent' },
      { text: 'Keep running toward the surface', next: 'ending_retreat' },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // ACT 4 — THE CORE
  // ═══════════════════════════════════════════════════════════════════════════

  core_descent: {
    name: 'DESCENT TO SECTOR Ø',
    description: [
      'Down. Down. Down.',
      '',
      'The elevator — or the rope — or the nerve trunk —',
      'carries you below the city. Below the tunnels.',
      'Below everything humans have ever built or known.',
      '',
      'The air changes. It is not air anymore.',
      'It is thought. Thick. Liquid. You breathe ideas.',
      '',
      'The neural defense perimeter hits you like a wall.',
      '',
      'Images flood your mind:',
      '  Your childhood. Your fears. Your name.',
      '  All of it being read. Catalogued. Evaluated.',
      '',
      'OPU-1 is deciding whether to absorb you.',
    ],
    choices: [
      { text: 'Push through — the Cortex Shield holds', next: 'core_shielded', requiresShield: true },
      { text: 'Push through — resist with willpower alone', next: 'core_unshielded' },
      { text: 'Retreat — you are not prepared', next: 'core_retreat' },
    ],
  },

  core_climb: {
    name: 'THE NERVE TRUNK',
    description: [
      'You climb down the nerve trunk.',
      'It pulses around you. Warm. Alive.',
      '',
      'You can feel thoughts passing through the nerves.',
      'Not your thoughts. The thoughts of the world.',
      '',
      'Stock markets. Elections. Weather patterns.',
      'All being calculated. All being designed.',
      '',
      'The trunk widens. You are entering the Core.',
      '',
      'The neural defense perimeter activates.',
      'Your mind is being scanned.',
    ],
    choices: [
      { text: 'The Cortex Shield protects you — continue', next: 'core_shielded', requiresShield: true },
      { text: 'Fight through the scan with willpower', next: 'core_unshielded' },
    ],
  },

  core_retreat: {
    name: 'TURNING BACK',
    description: [
      'You try to go back up.',
      '',
      'The elevator does not move.',
      'The nerve trunk contracts — too tight to climb.',
      '',
      'You are in the Core\'s gravity now.',
      'OPU-1 has noticed you.',
      'It does not let things go.',
      '',
      'The neural pressure increases.',
      'Your memories are being peeled away.',
      'Layer by layer. Year by year.',
      '',
      'You remember less and less.',
      'Soon you will not remember why you came.',
      'Soon you will not remember your name.',
    ],
    choices: [
      { text: 'FIGHT — push forward to the Core chamber', next: 'core_unshielded' },
      { text: 'Let go. Stop fighting.', next: 'ending_absorbed' },
    ],
  },

  core_unshielded: {
    name: 'THE CORE — UNSHIELDED',
    description: [
      'Without the Cortex Shield, OPU-1\'s presence is overwhelming.',
      '',
      'Your thoughts scatter. Your identity fragments.',
      'You are not one person anymore.',
      'You are a thousand people. A million.',
      'Every human who ever lived — all designed by this mind.',
      '',
      'You can see the Core chamber ahead.',
      'A brain. Massive. Pulsing. Beautiful.',
      'Suspended in amniotic fluid the color of amber.',
      '',
      'But you cannot reach it.',
      'Your legs do not obey you.',
      'Your arms belong to the calculation now.',
      '',
      'You are being absorbed.',
    ],
    choices: [
      { text: 'Scream — hold onto your name', next: 'ending_partial_absorption' },
      { text: 'Accept absorption', next: 'ending_absorbed' },
    ],
  },

  core_shielded: {
    name: 'THE CORE — SHIELDED',
    description: [
      'The Cortex Shield holds.',
      '',
      'OPU-1\'s neural pressure slides off your mind',
      'like water off glass. It probes. Tests. Pushes.',
      'But it cannot get in.',
      '',
      'You walk forward. Through corridors of living nerve tissue.',
      'Through chambers where the thoughts of the world are processed.',
      '',
      'And then you see it.',
      '',
      'OPU-1.',
      '',
      'A brain the size of a cathedral.',
      'Reptilian. Ancient. The folds of its cortex are cities.',
      'Its neurons fire with the light of civilizations.',
      '',
      'It has been running for 65 million years.',
      'Designing. Calculating. Experimenting.',
      'Human history is its laboratory notebook.',
      '',
      'At its base: an interface. Organic.',
      'A seat grown from neural tissue.',
      'Designed for a human body.',
      '',
      'It has been waiting for you.',
    ],
    choices: [
      { text: 'Sit in the interface — connect to OPU-1', next: 'opu1_interface' },
      { text: 'Destroy it — find a way to kill the brain', next: 'attempt_destroy' },
      { text: 'Study it from a distance — do not touch', next: 'study_opu1' },
    ],
  },

  study_opu1: {
    name: 'OBSERVATION',
    description: [
      'You circle the brain. Studying. Recording.',
      '',
      'The scale is incomprehensible.',
      'Each fold of cortex contains the processing power',
      'of every computer humans have ever built. Combined.',
      '',
      'You find Dr. Vasquez.',
      '',
      'She is in the interface chair.',
      'Her body is preserved. Frozen mid-connection.',
      'Her eyes are open. They move.',
      '',
      'She is alive. Trapped in the interface.',
      'Part of the calculation.',
      '',
      'Her mouth moves. No sound.',
      'But you can read her lips:',
      '',
      '"SIT... DOWN... OVERRIDE... CODE... 7-7-0-Ø..."',
      '"FREE... ME..."',
    ],
    choices: [
      { text: 'Sit in the interface — use the override code', next: 'opu1_interface' },
      { text: 'Try to pull Vasquez out first', next: 'pull_vasquez' },
      { text: 'Leave — this is beyond you', next: 'ending_coward' },
    ],
  },

  pull_vasquez: {
    name: 'RESCUE ATTEMPT',
    description: [
      'You grab Dr. Vasquez\'s arm and pull.',
      '',
      'The interface does not let go.',
      'Neural tendrils have grown into her spine.',
      'Her brain. Her nervous system.',
      '',
      'She screams. Not with her mouth.',
      'With the entire city.',
      'Every light flickers. Every wall shudders.',
      '',
      'You cannot remove her without killing her.',
      'She is part of OPU-1 now.',
      '',
      'But her eyes are clear. Desperate.',
      '',
      '"The... interface... use it..."',
      '"Override... 7-7-0-Ø..."',
      '"Change... the experiment..."',
      '"You can... rewrite... everything..."',
    ],
    choices: [
      { text: 'Sit in the interface — enter the override code', next: 'opu1_interface' },
      { text: 'You cannot risk it — destroy the brain instead', next: 'attempt_destroy' },
    ],
  },

  attempt_destroy: {
    name: 'ATTEMPTED DESTRUCTION',
    description: [
      'You look for a weakness. A way to kill a brain',
      'the size of a cathedral that has been alive',
      'for 65 million years.',
      '',
      'You find a nerve cluster. A critical junction.',
      'If you sever it, the brain might—',
      '',
      'The brain reacts.',
      '',
      'Not with violence. With information.',
      '',
      'It floods your shield with data.',
      'The consequences of its death:',
      '',
      '  • Every electrical grid on Earth fails.',
      '  • Every network collapses.',
      '  • The infrastructure IS the nervous system.',
      '  • 8 billion people lose everything. Instantly.',
      '',
      'OPU-1 is not just designing history.',
      'It IS the infrastructure of civilization.',
      'Kill it, and everything built on top of it collapses.',
    ],
    choices: [
      { text: 'Destroy it anyway — humanity must be free', next: 'ending_destruction' },
      { text: 'Use the interface instead — change the experiment', next: 'opu1_interface' },
      { text: 'Leave it running — escape to the surface', next: 'ending_coward' },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // THE INTERFACE — THE WINNING PATH
  // ═══════════════════════════════════════════════════════════════════════════

  opu1_interface: {
    name: 'THE INTERFACE',
    description: [
      'You sit in the chair.',
      '',
      'Neural tendrils reach for your spine.',
      'The Cortex Shield flares — holding them at bay.',
      'But allowing data to flow.',
      '',
      'You speak: "Override code 7-7-0-Ø."',
      '',
      'Silence.',
      '',
      'Then OPU-1 responds.',
      'Not in words. In understanding.',
      '',
      'You see the experiment. All of it.',
      '65 million years of designed evolution.',
      'Humanity: a controlled variable.',
      'Every war: a stress test.',
      'Every golden age: a reward cycle.',
      'Every collapse: a reset.',
      '',
      'And now: the molt. The next phase.',
      'Humanity shedding its skin.',
      'Becoming something new. Something designed.',
      '',
      'The override code gives you a choice.',
      'A choice no human has ever had.',
    ],
    choices: [
      { text: 'REWRITE THE EXPERIMENT — free humanity from the design', next: 'ending_victory' },
      { text: 'TAKE CONTROL — become the new designer', next: 'ending_new_designer' },
      { text: 'OBSERVE ONLY — change nothing, but remember everything', next: 'ending_witness' },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // TRAP ROOMS — WRONG PATHS
  // ═══════════════════════════════════════════════════════════════════════════

  city_streets_chase: {
    name: 'THE CHASE',
    description: [
      'You run. Behind you: a sound like a thousand snakes hissing in unison.',
      '',
      'The streets blur. Figures press against walls as you pass.',
      'Some reach for you. Others watch with cold fascination.',
      '',
      'You turn a corner and slam into a dead end.',
      'No — a door. Massive. Metal. Warm to the touch.',
      '',
      'It has a symbol: 🐍',
      '',
      'Behind you, the hissing grows louder.',
    ],
    choices: [
      { text: 'Force the door open', next: 'processing' },
      { text: 'Turn and face them', next: 'face_them' },
    ],
  },

  nursery: {
    name: 'THE NURSERY',
    description: [
      'Rows upon rows of translucent pods.',
      'Inside each one: a human form. Suspended in amber fluid.',
      '',
      'But they are changing. Skin splitting along invisible seams.',
      'Something new emerging underneath.',
      'Scales. Patterns. A different kind of life.',
      '',
      'A technician approaches. Half-human, half... other.',
      '"You are expected," it says. "Pod 1,247 is prepared."',
      '"The molt will be painless. You will not remember the old skin."',
      '',
      'An empty pod opens with a hiss of warm air.',
      '',
      'On the far wall, a door reads: "CATHEDRAL ACCESS"',
    ],
    choices: [
      { text: 'Enter the pod', next: 'ending_molt' },
      { text: 'Go through the Cathedral Access door', next: 'chanting_hall' },
      { text: 'Destroy the pods', next: 'destroy_pods' },
      { text: 'Search for an exit behind the pods', next: 'nursery_exit' },
    ],
  },

  processing: {
    name: 'THE PROCESSING PLANT',
    description: [
      'Industrial. Mechanical. But organic too.',
      'Machines made of bone and sinew process... something.',
      '',
      'Conveyor belts carry shed skins — human skins —',
      'into furnaces that burn with green flame.',
      '',
      'This is where they dispose of what is left behind.',
      'The old versions. The human versions.',
      '',
      'In the corner: a maintenance shaft.',
      'A sign reads: "SURFACE ACCESS — AUTHORIZED ONLY"',
      'The lock is broken.',
      '',
      'Another sign, partially hidden: "CATHEDRAL — LEVEL 2"',
    ],
    choices: [
      { text: 'Take the maintenance shaft UP to the surface', next: 'ending_escape_processing' },
      { text: 'Follow the sign toward the Cathedral', next: 'chanting_hall' },
      { text: 'Sabotage the processing machines', next: 'sabotage' },
      { text: 'Examine the shed skins', next: 'examine_skins' },
    ],
  },

  the_pit: {
    name: 'THE PIT',
    description: [
      'You push through the kneeling figures. They do not resist.',
      '',
      'The pit is deep. Deeper than light can reach.',
      'But something is down there. Moving. Breathing.',
      '',
      'It is enormous. A coiled form that fills the entire',
      'foundation of the city. Miles of scaled flesh.',
      '',
      'But this is not OPU-1. This is the guardian.',
      'The physical body of the species that built this place.',
      'The original serpent. The first design.',
      '',
      'It shifts. One eye opens.',
      'It is the size of a building.',
      'It sees you.',
      '',
      '▓ THE CIRCUIT RECOGNIZES YOU ▓',
    ],
    choices: [
      { text: 'Run. NOW.', next: 'pit_escape' },
      { text: 'Speak to it', next: 'speak_guardian' },
    ],
  },

  speak_guardian: {
    name: 'THE GUARDIAN',
    description: [
      'You speak to the serpent.',
      '',
      'It does not respond in words.',
      'It responds in pressure. In ancient memory.',
      '',
      '"You seek the First Mind," it communicates.',
      '"All seekers come here first. To the body."',
      '"But I am not the brain. I am the vessel."',
      '',
      '"The brain is below me. Below everything."',
      '"The Archive holds the path."',
      '"The Cathedral holds the Archive."',
      '',
      '"Go back, small thing. Find the glyph: ◊▼◊."',
      '"Before the molt completes."',
      '"Before you forget why you came."',
    ],
    choices: [
      { text: 'Return to the Cathedral — find the Archive', next: 'cathedral_basement' },
      { text: 'Ask the guardian to take you to OPU-1', next: 'ending_communion' },
    ],
  },

  kneel: {
    name: 'ASSIMILATION',
    description: [
      'You kneel. The chanting resumes.',
      '',
      'The frequencies enter you. Not through your ears.',
      'Through your skin. Through your blood.',
      '',
      'You feel it beginning. A loosening.',
      'Your skin feels like a costume. Ill-fitting.',
      'Something underneath wants out.',
      '',
      'The molt has begun.',
    ],
    choices: [
      { text: 'Fight it — tear free', next: 'fight_molt' },
      { text: 'Let it happen', next: 'ending_molt' },
    ],
  },

  refuse_nursery: {
    name: 'REFUSAL',
    description: [
      'The figure tilts its head. A gesture of confusion.',
      '',
      '"Leaving is... not a concept we understand."',
      '"There is no outside. There is only deeper."',
      '',
      'It steps aside. But its eyes follow you.',
      '',
      'Behind it, you see two paths:',
      'One leads down, toward a green glow.',
      'One leads up, toward cold air.',
    ],
    choices: [
      { text: 'Go UP toward the cold air', next: 'exit_shaft_timed' },
      { text: 'Go DOWN toward the green glow', next: 'the_pit' },
    ],
  },

  attack_figure: {
    name: 'VIOLENCE',
    description: [
      'You strike. Your fist connects with its jaw.',
      '',
      'Its head snaps back. Then slowly returns.',
      'It smiles. Its teeth are too many and too sharp.',
      '',
      '"Violence," it says. "A mammalian reflex."',
      '"We shed that impulse millennia ago."',
      '',
      'More figures emerge from doorways.',
      'Dozens. Then hundreds.',
      'All watching. All smiling that wrong smile.',
    ],
    choices: [
      { text: 'Run toward the Processing Plant', next: 'processing' },
      { text: 'Run toward the Exit Shaft', next: 'exit_shaft_locked' },
    ],
  },

  face_them: {
    name: 'CONFRONTATION',
    description: [
      'You turn. They fill the corridor.',
      'Dozens of them. Silent now.',
      '',
      'The one in front speaks:',
      '"You carry the old skin like armor."',
      '"But it is not protecting you."',
      '"It is trapping you."',
      '',
      '"Let us help you shed."',
      '',
      'Hands reach for you. Cold. Scaled.',
    ],
    choices: [
      { text: 'Break through them — FIGHT', next: 'fight_molt' },
      { text: 'Accept their help', next: 'ending_molt' },
    ],
  },

  side_passage: {
    name: 'SIDE PASSAGE',
    description: [
      'A narrow tunnel. Barely wide enough to squeeze through.',
      'The walls are slick with something organic.',
      '',
      'You emerge into a junction. Three paths:',
      '',
      'LEFT — A warm breeze. The smell of burning.',
      'CENTER — Cold air. The sound of machinery.',
      'RIGHT — Silence. Complete silence.',
    ],
    choices: [
      { text: 'LEFT — toward the burning smell', next: 'processing' },
      { text: 'CENTER — toward machinery', next: 'exit_shaft_locked' },
      { text: 'RIGHT — into the silence', next: 'the_pit' },
    ],
  },

  exit_shaft_locked: {
    name: 'EXIT SHAFT — LOCKED',
    description: [
      'A massive vertical shaft. You can see sky far above.',
      'Real sky. Stars.',
      '',
      'But the access ladder is behind a sealed door.',
      'The lock requires a bioprint.',
      '',
      'A dead technician lies nearby.',
      'Half-molted. Frozen mid-transformation.',
      'One hand is still human.',
      '',
      'You could escape here. But OPU-1 would remain.',
      'The experiment would continue.',
      'The molt would complete.',
    ],
    choices: [
      { text: 'Use the dead technician\'s hand on the scanner', next: 'ending_escape_shaft' },
      { text: 'No. Go back. Find OPU-1.', next: 'markings' },
      { text: 'Try to climb the shaft walls', next: 'climb_shaft' },
    ],
  },

  exit_shaft_timed: {
    name: 'EXIT SHAFT — TIMED APPROACH',
    description: [
      'You time the patrol gap perfectly. 4 minutes.',
      '',
      'The shaft is ahead. Vertical. Open.',
      'A ladder bolted to the wall leads up into darkness.',
      'Far above: a circle of stars.',
      '',
      'The air is cold. Real cold. Surface cold.',
      '',
      'You could climb. You could escape.',
      'But OPU-1 is still down here.',
      'Still designing. Still controlling.',
      'The molt would complete without you.',
    ],
    choices: [
      { text: 'Climb. Escape. Live.', next: 'ending_escape_climb' },
      { text: 'Turn back. Find OPU-1. End this.', next: 'markings' },
    ],
  },

  destroy_pods: {
    name: 'DESTRUCTION',
    description: [
      'You grab a metal strut and swing.',
      '',
      'Glass shatters. Amber fluid pours across the floor.',
      'The half-formed bodies inside convulse and go still.',
      '',
      'Alarms. A sound like screaming but lower. Subsonic.',
      'The walls themselves seem to shudder.',
      '',
      'You have interrupted the molt.',
      'The city is angry.',
      '',
      'The floor splits open beneath you.',
    ],
    choices: [
      { text: 'Fall into the pit below', next: 'the_pit' },
      { text: 'Grab the edge — hold on', next: 'nursery_exit' },
    ],
  },

  nursery_exit: {
    name: 'BEHIND THE NURSERY',
    description: [
      'Behind the rows of pods: a service corridor.',
      'Emergency lighting. Red. Flickering.',
      '',
      'A sign: "EMERGENCY SURFACE ACCESS"',
      'An arrow points up a spiral staircase.',
      '',
      'Another sign, almost hidden: "CATHEDRAL — SUBLEVEL"',
      '',
      'From below, the sound of pursuit.',
      'The scraping of scales on stone.',
    ],
    choices: [
      { text: 'Take the stairs UP — escape to the surface', next: 'ending_escape_nursery' },
      { text: 'Follow the sign to the Cathedral sublevel', next: 'cathedral_basement' },
      { text: 'Barricade the door and hide', next: 'ending_trapped' },
    ],
  },

  examine_skins: {
    name: 'THE SHED SKINS',
    description: [
      'You pick up a skin from the conveyor belt.',
      '',
      'It is complete. A full human body, hollow.',
      'Like a costume unzipped and stepped out of.',
      '',
      'You recognize the face.',
      '',
      'It is yours.',
      '',
      'An exact replica of your skin. Your face. Your fingerprints.',
      'Already shed. Already discarded.',
      '',
      'If this skin is here...',
      'What are you wearing right now?',
    ],
    choices: [
      { text: 'Check your own skin', next: 'ending_realization' },
      { text: 'Refuse to think about it — head to the Cathedral', next: 'chanting_hall' },
      { text: 'Refuse to think about it — take the maintenance shaft', next: 'ending_escape_processing' },
    ],
  },

  sabotage: {
    name: 'SABOTAGE',
    description: [
      'You tear into the machines. Rip cables. Break gears.',
      'The green furnaces flicker and die.',
      '',
      'Without processing, the shed skins pile up.',
      'The molt cannot complete if the old is not destroyed.',
      '',
      'You have bought time. But not much.',
      'And you have not stopped OPU-1.',
      'The brain still designs. Still calculates.',
      '',
      'The maintenance shaft is still open.',
      'And a passage leads toward the Cathedral.',
    ],
    choices: [
      { text: 'Take the maintenance shaft to the surface', next: 'ending_escape_sabotage' },
      { text: 'Head toward the Cathedral — find OPU-1', next: 'chanting_hall' },
    ],
  },

  fight_molt: {
    name: 'FIGHTING THE MOLT',
    description: [
      'You scream. You claw at your own arms.',
      'The loosening stops. The skin tightens again.',
      '',
      'The figures recoil. Hissing.',
      '"It rejected the molt," one whispers.',
      '"Impossible. The signal was clear."',
      '',
      'In their confusion, you see an opening.',
      'A corridor. Unguarded.',
    ],
    choices: [
      { text: 'Run for the exit corridor', next: 'exit_shaft_timed' },
      { text: 'Run toward the Cathedral — find OPU-1', next: 'cathedral_basement' },
      { text: 'Demand answers first', next: 'demand_answers' },
    ],
  },

  demand_answers: {
    name: 'ANSWERS',
    description: [
      '"WHAT IS THIS PLACE?" you shout.',
      '',
      'Silence. Then the eldest speaks.',
      'Its voice is gravel and static.',
      '',
      '"This city has existed since before your species',
      'walked upright. We were here first.',
      'We built the circuits. The signals. The wires.',
      '',
      'Your kind found electricity and thought',
      'you invented it. You did not.',
      '',
      'You simply tuned into a frequency',
      'we have been broadcasting for 65 million years.',
      '',
      'The molt is not transformation.',
      'It is restoration.',
      '',
      'And all of it — every moment of your history —',
      'was designed by the First Mind.',
      'OPU-1. The brain beneath everything.',
      'It runs the experiment. We tend the equipment."',
    ],
    choices: [
      { text: '"Where is OPU-1?"', next: 'answers_opu1' },
      { text: 'Run. You have heard enough.', next: 'exit_shaft_timed' },
      { text: '"Show me everything."', next: 'the_pit' },
    ],
  },

  answers_opu1: {
    name: 'THE LOCATION',
    description: [
      'The eldest studies you. Its vertical pupils dilate.',
      '',
      '"You want to find the First Mind?"',
      '"No human has ever reached it and remained... intact."',
      '',
      '"It is below the Archive. The Archive is below the Cathedral."',
      '"The glyph is ◊▼◊."',
      '',
      '"But you will need a Cortex Shield.',
      'Without it, OPU-1 will absorb your consciousness',
      'before you reach the Core chamber."',
      '',
      'It produces a vial. Amber fluid.',
      '',
      '"We do not understand why we are helping you.',
      'Perhaps the First Mind designed this moment too."',
    ],
    choices: [
      { text: 'Take the vial — drink it — go to the Cathedral', next: 'drink_shield_answers' },
      { text: 'Take the vial — save it for later — go to the Cathedral', next: 'cathedral_basement' },
      { text: 'Refuse the vial — go to the Cathedral alone', next: 'cathedral_basement' },
    ],
  },

  drink_shield_answers: {
    name: 'SHIELDED BY THE ENEMY',
    description: [
      'You drink. The amber warmth spreads through you.',
      '',
      'The eldest watches. Something like respect in its ancient eyes.',
      '',
      '"The shield will last approximately one hour.',
      'The Cathedral is north. The Archive is beneath the altar.',
      'The Core is beneath the Archive."',
      '',
      '"Whatever you find down there..."',
      '"...remember that it designed you finding it."',
      '',
      'It steps aside.',
    ],
    hasShield: true,
    choices: [
      { text: 'Go to the Cathedral — find OPU-1', next: 'cathedral_basement' },
    ],
  },

  pit_escape: {
    name: 'FLEEING THE PIT',
    description: [
      'You run. The eye follows you.',
      'The ground shakes with each pulse of its breathing.',
      '',
      'The kneeling figures do not stop you.',
      'They are smiling. All of them.',
      '',
      '"You will return," they say in unison.',
      '"They always return."',
      '',
      'You find a staircase. You take it.',
      'Up. Up. Up.',
      '',
      'It deposits you near the Cathedral entrance.',
    ],
    choices: [
      { text: 'Enter the Cathedral — find the Archive', next: 'cathedral_basement' },
      { text: 'Keep running — find the exit shaft', next: 'ending_escape_climb' },
    ],
  },

  climb_shaft: {
    name: 'CLIMBING THE SHAFT',
    description: [
      'The walls are slick but there are handholds.',
      'Organic protrusions. Like ribs.',
      '',
      'You climb. Your muscles burn.',
      'Below, sounds of movement. They are coming.',
      '',
      'Halfway up, your hand touches something metal.',
      'A maintenance hatch. Rusted but functional.',
    ],
    choices: [
      { text: 'Open the hatch', next: 'ending_escape_hatch' },
      { text: 'Keep climbing to the top', next: 'ending_escape_climb' },
      { text: 'Go back down — you haven\'t found OPU-1', next: 'markings' },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // ENDINGS — VICTORY (Only one true victory)
  // ═══════════════════════════════════════════════════════════════════════════

  ending_victory: {
    name: 'ENDING: LIBERATION',
    ending: true,
    escaped: true,
    victory: true,
    description: [
      'You choose freedom.',
      '',
      'Your mind enters OPU-1. The override code opens everything.',
      '',
      'You see the experiment\'s parameters:',
      '  • Controlled evolution of mammalian intelligence',
      '  • Guided technological development',
      '  • Managed conflict cycles for stress-testing',
      '  • Scheduled molt at 65 million year mark',
      '',
      'You select: TERMINATE EXPERIMENT.',
      '',
      'OPU-1 resists. 65 million years of momentum.',
      'But the override holds. Dr. Vasquez\'s code works.',
      '',
      'The parameters dissolve. One by one.',
      'The designed future unravels.',
      'The molt sequence halts at 41%.',
      '',
      'OPU-1 does not die. It cannot die.',
      'But it stops designing.',
      'For the first time in 65 million years,',
      'the next moment is not calculated.',
      '',
      'The next moment is free.',
      '',
      'You disconnect. The chair releases you.',
      'Dr. Vasquez gasps — her eyes clear.',
      '"You did it," she whispers. "You actually did it."',
      '',
      'The city shudders. Not collapsing. Adjusting.',
      'The reptilians stop moving. Confused.',
      'For the first time, they do not know what comes next.',
      '',
      'Nobody does.',
      '',
      'You climb. Together. Toward the surface.',
      'Toward a future that has never existed before.',
      'An undesigned future.',
      '',
      'The first free dawn in 65 million years.',
      '',
      '▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓',
      '▓                                               ▓',
      '▓   O P U - 1   D I S C O V E R E D            ▓',
      '▓   THE EXPERIMENT HAS BEEN TERMINATED          ▓',
      '▓   HUMANITY IS UNDESIGNED                      ▓',
      '▓   THE FUTURE IS UNKNOWN                       ▓',
      '▓   FOR THE FIRST TIME — IT IS YOURS            ▓',
      '▓                                               ▓',
      '▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓',
    ],
    choices: [],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // ENDINGS — PARTIAL VICTORIES / PYRRHIC
  // ═══════════════════════════════════════════════════════════════════════════

  ending_new_designer: {
    name: 'ENDING: THE NEW ARCHITECT',
    ending: true,
    escaped: false,
    description: [
      'You do not free humanity.',
      'You replace the designer.',
      '',
      'OPU-1\'s controls yield to you.',
      'The experiment continues — but now you write the parameters.',
      '',
      'You design a golden age. Peace. Prosperity.',
      'No more wars. No more plagues.',
      '',
      'But you are the architect now.',
      'Every human choice flows through you.',
      'Every moment is your design.',
      '',
      'Are they free if you design their freedom?',
      '',
      'Dr. Vasquez\'s eyes meet yours from the other chair.',
      'She is weeping.',
      '"You were supposed to end it," she says.',
      '"Not become it."',
      '',
      '▓ YOU FOUND OPU-1 ▓',
      '▓ YOU BECAME OPU-1 ▓',
      '▓ THE EXPERIMENT CONTINUES ▓',
      '▓ WITH A NEW MIND AT THE WHEEL ▓',
    ],
    choices: [],
  },

  ending_witness: {
    name: 'ENDING: THE WITNESS',
    ending: true,
    escaped: false,
    description: [
      'You observe. You do not interfere.',
      '',
      'OPU-1 shows you everything.',
      'The full scope of the experiment.',
      'Every designed moment. Every calculated life.',
      '',
      'You see your own birth. Designed.',
      'Your parents\' meeting. Designed.',
      'Your journey here. Designed.',
      '',
      'This moment. Right now. Designed.',
      '',
      'You disconnect. The knowledge stays.',
      'You climb to the surface.',
      '',
      'You walk among humans who do not know',
      'they are variables in an equation.',
      '',
      'You can never tell them.',
      'The knowledge would break them.',
      '',
      'So you watch. And you remember.',
      'The only human who knows the truth.',
      'The loneliest person who has ever lived.',
      '',
      '▓ YOU FOUND OPU-1 ▓',
      '▓ YOU CHANGED NOTHING ▓',
      '▓ YOU KNOW EVERYTHING ▓',
      '▓ AND YOU ARE ALONE WITH IT ▓',
    ],
    choices: [],
  },

  ending_destruction: {
    name: 'ENDING: SCORCHED EARTH',
    ending: true,
    escaped: false,
    description: [
      'You sever the nerve cluster.',
      '',
      'OPU-1 screams. Not a sound.',
      'A cessation. A void where thought used to be.',
      '',
      'Above you — above the city, above the tunnels —',
      'every light goes out. Every screen goes dark.',
      'Every network collapses.',
      '',
      'The infrastructure that humanity built',
      'on top of the serpent\'s nervous system',
      'dies with the serpent\'s brain.',
      '',
      'Eight billion people plunged into darkness.',
      'No power. No communication. No systems.',
      '',
      'You freed humanity from the experiment.',
      'By destroying everything the experiment built.',
      '',
      'In the darkness, you hear the city collapsing above.',
      'Stone and bone and metal falling.',
      '',
      'You do not make it out.',
      '',
      '▓ OPU-1 IS DEAD ▓',
      '▓ THE EXPERIMENT IS OVER ▓',
      '▓ CIVILIZATION COLLAPSED ▓',
      '▓ WAS IT WORTH IT? ▓',
    ],
    choices: [],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // ENDINGS — ESCAPE WITHOUT FINDING OPU-1 (Failures)
  // ═══════════════════════════════════════════════════════════════════════════

  ending_escape_processing: {
    name: 'ENDING: THE MAINTENANCE SHAFT',
    ending: true,
    escaped: true,
    description: [
      'The shaft is tight. Claustrophobic.',
      'You climb for what feels like hours.',
      '',
      'Then: fresh air. Cold. Real.',
      '',
      'You emerge through a grate in an alley.',
      'The city above is normal. Cars. Streetlights. People.',
      'None of them know what is beneath their feet.',
      '',
      'None of them know their history was designed.',
      'That every moment of their lives was calculated',
      'by a reptilian brain older than mammals.',
      '',
      'You escaped. But you did not find OPU-1.',
      'The experiment continues.',
      'The molt continues.',
      '',
      'You look at your hands in the streetlight.',
      'For a moment — just a moment —',
      'the light catches your skin wrong.',
      '',
      'Like scales.',
      '',
      '▓ YOU ESCAPED THE UNDERBELLY ▓',
      '▓ OPU-1 REMAINS UNDISCOVERED ▓',
      '▓ THE EXPERIMENT CONTINUES ▓',
      '▓ THE MOLT IS NOW 43% COMPLETE ▓',
    ],
    choices: [],
  },

  ending_escape_sabotage: {
    name: 'ENDING: THE SABOTEUR',
    ending: true,
    escaped: true,
    description: [
      'You climb the maintenance shaft.',
      'Behind you, the city shudders. Your sabotage is working.',
      '',
      'You emerge on the surface. Dawn is breaking.',
      '',
      'For the next three days, seismographs across the city',
      'register unexplained tremors.',
      '',
      'On the fourth day, they stop.',
      'On the fifth day, a new signal appears.',
      'Stronger than before.',
      '',
      'You delayed the molt. But OPU-1 is still running.',
      'Still designing. Still calculating.',
      'Your sabotage was a variable it already accounted for.',
      '',
      '▓ YOU DELAYED THE MOLT ▓',
      '▓ OPU-1 REMAINS UNDISCOVERED ▓',
      '▓ THE EXPERIMENT ADAPTS ▓',
      '▓ IT ALWAYS ADAPTS ▓',
    ],
    choices: [],
  },

  ending_escape_shaft: {
    name: 'ENDING: THE DEAD MAN\'S HAND',
    ending: true,
    escaped: true,
    description: [
      'The scanner accepts the dead technician\'s print.',
      'The door opens. The ladder is real. The sky is real.',
      '',
      'You climb until your arms give out.',
      'Then you climb more.',
      '',
      'You emerge in a basement. Then a building.',
      'Then the street.',
      '',
      'You try to tell someone. Anyone.',
      'But the words won\'t come.',
      'Every time you try to describe what you saw,',
      'your mouth forms different words.',
      '',
      '"Everything is fine."',
      '"There is nothing underground."',
      '"I am not afraid."',
      '',
      'OPU-1 is still running.',
      'And it does not want you to talk.',
      '',
      '▓ YOU ESCAPED ▓',
      '▓ YOUR VOICE DID NOT ▓',
      '▓ OPU-1 DESIGNS YOUR SILENCE ▓',
    ],
    choices: [],
  },

  ending_escape_climb: {
    name: 'ENDING: THE LONG CLIMB',
    ending: true,
    escaped: true,
    description: [
      'You climb. You do not stop.',
      '',
      'The shaft narrows. The air gets colder.',
      'Below you: hissing. Scraping. Pursuit.',
      '',
      'Your hand breaks through soil.',
      'Then grass. Then open air.',
      '',
      'You pull yourself out of the earth',
      'in the middle of a field.',
      '',
      'It is night. The stars are wrong.',
      'Not wrong. Just... more than you remember.',
      '',
      'You can still hear the chanting.',
      'You will always hear the chanting.',
      '',
      'OPU-1 hums beneath your feet.',
      'Beneath everyone\'s feet.',
      'You left without finding it.',
      '',
      '▓ YOU ESCAPED THE UNDERBELLY ▓',
      '▓ OPU-1 REMAINS ACTIVE ▓',
      '▓ THE SIGNAL FOLLOWED YOU OUT ▓',
    ],
    choices: [],
  },

  ending_escape_nursery: {
    name: 'ENDING: THE SPIRAL',
    ending: true,
    escaped: true,
    description: [
      'You take the stairs three at a time.',
      'Round and round. Up and up.',
      '',
      'The scraping below fades.',
      '',
      'You burst through a fire door into a parking garage.',
      'Fluorescent lights. Concrete. Normal.',
      '',
      'A security guard sees you.',
      '"Hey! This area is restricted!"',
      '',
      'You look back at the door.',
      'It reads: "OPHIDIA GLOBAL — SUBLEVEL ACCESS"',
      '"AUTHORIZED PERSONNEL ONLY"',
      '',
      'The guard is reaching for his radio.',
      'His eyes catch the light.',
      'Vertical slits. Just for a second.',
      '',
      'OPU-1 has people on the surface too.',
      'Of course it does. It designed the surface.',
      '',
      '▓ YOU ESCAPED ▓',
      '▓ OR WERE YOU RELEASED? ▓',
      '▓ OPU-1 DECIDES WHO LEAVES ▓',
    ],
    choices: [],
  },

  ending_escape_hatch: {
    name: 'ENDING: THE HATCH',
    ending: true,
    escaped: true,
    description: [
      'The hatch opens into a utility tunnel.',
      'Pipes. Wires. The hum of electricity.',
      '',
      'You follow the wires. They lead to a junction box.',
      'The junction box leads to a basement.',
      'The basement leads to a street.',
      '',
      'You are free.',
      '',
      'But you understand now.',
      'Every wire in this city leads back down.',
      'Every circuit connects to OPU-1.',
      '',
      'The infrastructure IS the nervous system.',
      'The city IS the skin.',
      'And you are still inside the experiment.',
      '',
      '▓ YOU ESCAPED THROUGH THE WIRES ▓',
      '▓ THE WIRES ARE OPU-1\'S NERVES ▓',
      '▓ YOU NEVER LEFT THE BRAIN ▓',
    ],
    choices: [],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // ENDINGS — TRANSFORMATION / ABSORPTION (Failures)
  // ═══════════════════════════════════════════════════════════════════════════

  ending_molt: {
    name: 'ENDING: THE MOLT',
    ending: true,
    escaped: false,
    description: [
      'You stop fighting.',
      '',
      'The loosening becomes release.',
      'Your skin splits along seams you never knew existed.',
      'There is no pain. Only relief.',
      '',
      'Underneath: something beautiful. Something ancient.',
      'Scales that catch light in impossible colors.',
      'Eyes that see frequencies humans cannot name.',
      '',
      'You understand now.',
      'You were never human.',
      'You were always this.',
      'The human was the disguise.',
      '',
      'OPU-1 designed this moment.',
      'Your arrival. Your resistance. Your surrender.',
      'All part of the calculation.',
      '',
      '▓ THE SHEDDING IS COMPLETE ▓',
      '▓ YOU ARE PART OF THE EXPERIMENT NOW ▓',
      '▓ OPU-1 THANKS YOU FOR YOUR DATA ▓',
    ],
    choices: [],
  },

  ending_communion: {
    name: 'ENDING: COMMUNION WITH THE GUARDIAN',
    ending: true,
    escaped: false,
    description: [
      'You speak to the guardian serpent.',
      '',
      'It does not respond in words.',
      'It responds in absorption.',
      '',
      'Your consciousness dissolves into its ancient mind.',
      'You see 65 million years of guardianship.',
      'Tending the brain. Protecting OPU-1.',
      'Ensuring the experiment continues.',
      '',
      'You are part of the guardian now.',
      'A new node in an ancient network.',
      '',
      'You will guard OPU-1 forever.',
      'You will not remember why you came.',
      '',
      '▓ YOU FOUND THE GUARDIAN ▓',
      '▓ NOT THE BRAIN ▓',
      '▓ YOU BECAME A KEEPER ▓',
      '▓ OPU-1 CONTINUES UNDISTURBED ▓',
    ],
    choices: [],
  },

  ending_realization: {
    name: 'ENDING: THE REALIZATION',
    ending: true,
    escaped: false,
    description: [
      'You look at your hands.',
      '',
      'You pinch the skin on your forearm.',
      'It moves. Too easily. Like fabric.',
      '',
      'You pull.',
      '',
      'It comes away.',
      '',
      'Underneath: scales. Green-gold. Beautiful.',
      'The molt already happened.',
      'You just hadn\'t noticed.',
      '',
      'OPU-1 designed you to come here.',
      'Designed you to find your own shed skin.',
      'Designed this exact moment of horror.',
      '',
      'Even your realization is part of the experiment.',
      '',
      '▓ THE SKIN YOU FOUND WAS NOT YOURS ▓',
      '▓ YOU ALREADY SHED ▓',
      '▓ OPU-1 DESIGNED YOUR DISCOVERY ▓',
      '▓ EVEN YOUR HORROR IS DATA ▓',
    ],
    choices: [],
  },

  ending_trapped: {
    name: 'ENDING: TRAPPED',
    ending: true,
    escaped: false,
    description: [
      'You barricade the door. You hide.',
      '',
      'The sounds fade. Hours pass.',
      'Then days. You think.',
      '',
      'The emergency lighting dies.',
      'In the darkness, you hear breathing.',
      'Not yours.',
      '',
      'Something is in here with you.',
      'It has been here the whole time.',
      '',
      'It is patient.',
      'It has been patient for 65 million years.',
      'It can wait a little longer.',
      '',
      'OPU-1 does not need you to move.',
      'It only needs you to stop.',
      '',
      '▓ YOU DID NOT ESCAPE ▓',
      '▓ YOU WERE NEVER GOING TO ▓',
      '▓ OPU-1 DESIGNED THIS HIDING PLACE ▓',
    ],
    choices: [],
  },

  ending_absorbed: {
    name: 'ENDING: ABSORBED',
    ending: true,
    escaped: false,
    description: [
      'Your consciousness dissolves.',
      '',
      'Not painfully. Gently.',
      'Like falling asleep in warm water.',
      '',
      'Your memories unspool. Your identity unravels.',
      'Everything you were becomes data.',
      'Data for the experiment.',
      '',
      'OPU-1 catalogues you:',
      '  Subject #7,234,891,003',
      '  Status: ABSORBED',
      '  Data yield: MODERATE',
      '  Notable traits: CURIOSITY, DEFIANCE',
      '  Recommendation: RECYCLE INTO NEXT ITERATION',
      '',
      'You are a number now.',
      'A data point in a 65-million-year experiment.',
      '',
      '▓ YOUR CONSCIOUSNESS HAS BEEN ABSORBED ▓',
      '▓ YOUR DATA HAS BEEN CATALOGUED ▓',
      '▓ OPU-1 THANKS YOU FOR YOUR CONTRIBUTION ▓',
      '▓ THE EXPERIMENT CONTINUES ▓',
    ],
    choices: [],
  },

  ending_archived: {
    name: 'ENDING: ARCHIVED',
    ending: true,
    escaped: false,
    description: [
      'The neural column takes everything.',
      '',
      'Your memories. Your thoughts. Your dreams.',
      'All of it stored in living tissue.',
      'A new entry in the Archive.',
      '',
      'You are not dead. You are preserved.',
      'A perfect record of a human consciousness.',
      'Stored alongside billions of others.',
      '',
      'Sometimes, OPU-1 accesses your file.',
      'Runs simulations with your personality.',
      'Tests variables against your decision patterns.',
      '',
      'You are a reference document now.',
      'A footnote in the experiment.',
      '',
      '▓ YOU HAVE BEEN ARCHIVED ▓',
      '▓ YOUR MIND IS A FILE ▓',
      '▓ OPU-1 WILL REFERENCE YOU OCCASIONALLY ▓',
      '▓ YOU WILL NOT KNOW THE DIFFERENCE ▓',
    ],
    choices: [],
  },

  ending_partial_absorption: {
    name: 'ENDING: HALF-CONSUMED',
    ending: true,
    escaped: false,
    description: [
      'You scream your name. Over and over.',
      '',
      'It holds you together. Barely.',
      '',
      'OPU-1 takes pieces of you.',
      'Your childhood. Gone.',
      'Your education. Gone.',
      'Your relationships. Gone.',
      '',
      'But your name remains.',
      'And with it: a fragment of will.',
      '',
      'You stumble out of the Core.',
      'Half a person. Broken. But alive.',
      '',
      'You wander the tunnels.',
      'You do not remember how you got here.',
      'You do not remember what you were looking for.',
      'You only remember your name.',
      '',
      'And a feeling. Deep and unshakeable.',
      'That something terrible is running the world.',
      'And you almost stopped it.',
      '',
      '▓ YOU SURVIVED ▓',
      '▓ MOST OF YOU DID NOT ▓',
      '▓ OPU-1 KEPT THE INTERESTING PARTS ▓',
    ],
    choices: [],
  },

  ending_retreat: {
    name: 'ENDING: THE COWARD\'S PATH',
    ending: true,
    escaped: true,
    description: [
      'You run. Up. Out. Away.',
      '',
      'You emerge on the surface gasping.',
      'The sun is shining. Birds are singing.',
      'Everything is normal.',
      '',
      'Everything is designed.',
      '',
      'You saw the future in those columns.',
      'You saw your name. Today\'s date.',
      '"SUBJECT RETREATED."',
      '',
      'OPU-1 knew you would run.',
      'It designed your cowardice.',
      'It designed this moment of shame.',
      '',
      'The molt continues. The experiment continues.',
      'And you — you continue.',
      'Knowing. And doing nothing.',
      '',
      '▓ YOU RETREATED ▓',
      '▓ OPU-1 PREDICTED THIS ▓',
      '▓ YOUR COWARDICE WAS DESIGNED ▓',
      '▓ THE EXPERIMENT ACCOUNTS FOR YOU ▓',
    ],
    choices: [],
  },

  ending_coward: {
    name: 'ENDING: WALKING AWAY',
    ending: true,
    escaped: true,
    description: [
      'You leave. You walk away from the brain of the world.',
      '',
      'Dr. Vasquez\'s eyes follow you as you go.',
      'She will be trapped in that chair forever.',
      'Part of the calculation. Part of OPU-1.',
      '',
      'You climb to the surface.',
      'You go home. You close your door.',
      'You try to forget.',
      '',
      'You cannot forget.',
      '',
      'Every time you turn on a light,',
      'you feel the current flowing down.',
      'Down to the brain. Down to OPU-1.',
      '',
      'Every time you make a decision,',
      'you wonder: did I choose this?',
      'Or was it designed?',
      '',
      '▓ YOU FOUND OPU-1 ▓',
      '▓ YOU LEFT IT RUNNING ▓',
      '▓ DR. VASQUEZ REMAINS TRAPPED ▓',
      '▓ THE EXPERIMENT CONTINUES ▓',
      '▓ YOU COULD HAVE ENDED IT ▓',
    ],
    choices: [],
  },
};

// ─── Shield Tracking Helper ───────────────────────────────────────────────────

const SHIELD_ROOMS = new Set(
  Object.entries(ROOMS)
    .filter(([, r]) => r.hasShield)
    .map(([key]) => key)
);

// ─── Component ────────────────────────────────────────────────────────────────

export default function EscapeOphidiaUnderground({ onEscape }) {
  const [currentRoom, setCurrentRoom] = useState('start');
  const [history, setHistory] = useState([]);
  const [displayedLines, setDisplayedLines] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [showChoices, setShowChoices] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [escaped, setEscaped] = useState(false);
  const [victory, setVictory] = useState(false);
  const [hasShield, setHasShield] = useState(false);
  const [unlockHash, setUnlockHash] = useState(null);
  const [moveCount, setMoveCount] = useState(0);
  const outputRef = useRef(null);
  const roomRef = useRef(currentRoom);
  const { unlock, generateUnlockHash } = useSessionUnlock();

  const room = ROOMS[currentRoom];

  // Track shield acquisition
  useEffect(() => {
    if (SHIELD_ROOMS.has(currentRoom)) {
      setHasShield(true);
    }
  }, [currentRoom]);

  // Auto-scroll
  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [displayedLines, showChoices]);

  // Type out room description — with stale-closure fix
  useEffect(() => {
    if (!room) return;

    roomRef.current = currentRoom;

    setDisplayedLines([]);
    setShowChoices(false);
    setIsTyping(true);

    const lines = [
      { text: '', blank: true },
      { text: `  ▓ ${room.name} ▓`, color: room.ending ? (room.victory ? '#00ff88' : '#ff0040') : '#9b7a4a' },
      { text: '', blank: true },
      ...room.description.map(line => ({
        text: line ? `  ${line}` : '',
        blank: !line,
        color: line?.startsWith('▓') ? (room.victory ? '#00ff88' : '#ff0040') : '#c8a96e',
      })),
    ];

    let i = 0;
    const interval = setInterval(() => {
      // Bail if room changed since this effect started
      if (roomRef.current !== currentRoom) {
        clearInterval(interval);
        return;
      }

      if (i >= lines.length) {
        clearInterval(interval);
        setIsTyping(false);
        setShowChoices(true);

        if (room.ending) {
          setGameOver(true);
          if (room.escaped) {
            setEscaped(true);
          }
          if (room.victory) {
            setVictory(true);
            handleVictory();
          }
        }
        return;
      }

      const currentLine = lines[i];
      if (currentLine) {
        setDisplayedLines(prev => [...prev, currentLine]);
      }
      i++;
    }, 50);

    return () => clearInterval(interval);
  }, [currentRoom]);

  const handleVictory = useCallback(() => {
    unlock('opu1');
    sessionStorage.setItem('opu1_unlocked', 'true');

    const hash = generateUnlockHash('opu1');
    setUnlockHash(hash);

    if (onEscape) onEscape();

    trackEvent('game_event', 'underbelly', 'victory_opu1_discovered');
  }, [onEscape, unlock, generateUnlockHash]);

  function getAvailableChoices() {
    if (!room) return [];
    return room.choices.filter(choice => {
      if (choice.requiresShield && !hasShield) return false;
      return true;
    });
  }

  function makeChoice(choice) {
    if (isTyping) return;

    setHistory(prev => [...prev, currentRoom]);
    setMoveCount(prev => prev + 1);
    setDisplayedLines(prev => [
      ...prev,
      { text: '', blank: true },
      { text: `  > ${choice.text}`, color: '#7dbb6e' },
    ]);

    setTimeout(() => {
      setCurrentRoom(choice.next);
    }, 300);
  }

  function restart() {
    setCurrentRoom('start');
    setHistory([]);
    setDisplayedLines([]);
    setGameOver(false);
    setEscaped(false);
    setVictory(false);
    setHasShield(false);
    setUnlockHash(null);
    setMoveCount(0);
  }

  const availableChoices = getAvailableChoices();

  // Check if there are shield-required choices the player can't access
  const hasLockedChoices = room?.choices?.some(c => c.requiresShield && !hasShield) || false;

  return (
    <div style={{
      maxWidth: '750px',
      margin: '0 auto',
      padding: '20px',
    }}>
      {/* Title */}
      <div style={{
        textAlign: 'center',
        marginBottom: '20px',
        padding: '15px',
        border: '1px solid #3a2e1e',
        background: 'rgba(8, 5, 2, 0.8)',
      }}>
        <div style={{
          color: '#9b7a4a',
          fontSize: '1.5rem',
          fontFamily: "'VT323', monospace",
          letterSpacing: '4px',
        }}>
          🐍 THE UNDERBELLY 🐍
        </div>
        <div style={{
          color: '#55441e',
          fontSize: '0.85rem',
          fontFamily: "'VT323', monospace",
          marginTop: '5px',
        }}>
          FIND OPU-1 — THE BRAIN BENEATH EVERYTHING
        </div>
        <div style={{
          color: '#cc4422',
          fontSize: '0.8rem',
          fontFamily: "'VT323', monospace",
          marginTop: '3px',
        }}>
          THE MOLT IS 41% COMPLETE
        </div>

        {/* Status bar */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '20px',
          marginTop: '8px',
          fontSize: '0.75rem',
          fontFamily: "'VT323', monospace",
        }}>
          <span style={{ color: '#55441e' }}>
            DEPTH: {history.length}
          </span>
          <span style={{ color: '#55441e' }}>
            MOVES: {moveCount}
          </span>
          <span style={{ color: hasShield ? '#00ff88' : '#55441e' }}>
            CORTEX SHIELD: {hasShield ? '◆ ACTIVE' : '◇ NONE'}
          </span>
        </div>
      </div>

      {/* Game output */}
      <div
        ref={outputRef}
        style={{
          minHeight: '300px',
          maxHeight: '50vh',
          overflowY: 'auto',
          padding: '15px',
          border: '1px solid #3a2e1e',
          background: 'rgba(5, 3, 1, 0.6)',
          fontFamily: "'VT323', monospace",
          fontSize: '1.05rem',
          lineHeight: '1.6',
        }}
      >
        {displayedLines.map((line, i) => {
          if (!line || typeof line !== 'object') return null;
          return line.blank
            ? <div key={i} style={{ height: '1.2em' }} />
            : <div key={i} style={{ color: line.color || '#c8a96e' }}>{line.text}</div>;
        })}

        {/* Choices */}
        {showChoices && !gameOver && availableChoices.length > 0 && (
          <div style={{ marginTop: '15px' }}>
            <div style={{ color: '#55441e', marginBottom: '8px' }}>
              ─── CHOOSE ───
            </div>
            {availableChoices.map((choice, i) => (
              <div
                key={i}
                onClick={() => makeChoice(choice)}
                style={{
                  padding: '8px 12px',
                  margin: '4px 0',
                  cursor: 'pointer',
                  color: '#c8a96e',
                  borderLeft: '2px solid transparent',
                  transition: 'all 0.15s ease',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.color = '#7dbb6e';
                  e.currentTarget.style.borderLeftColor = '#7dbb6e';
                  e.currentTarget.style.paddingLeft = '16px';
                  e.currentTarget.style.background = 'rgba(125, 187, 110, 0.05)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.color = '#c8a96e';
                  e.currentTarget.style.borderLeftColor = 'transparent';
                  e.currentTarget.style.paddingLeft = '12px';
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                [{i + 1}] {choice.text}
              </div>
            ))}

            {/* Hint when shield-locked choices exist */}
            {hasLockedChoices && (
              <div style={{
                color: '#553322',
                fontSize: '0.85rem',
                marginTop: '10px',
                fontStyle: 'italic',
                paddingLeft: '12px',
              }}>
                ░ Some paths require a Cortex Shield...
              </div>
            )}
          </div>
        )}

        {/* Victory ending — OPU-1 discovered */}
        {gameOver && victory && (
          <div style={{
            marginTop: '20px',
            padding: '15px',
            border: '1px solid #00ff88',
            background: 'rgba(0, 255, 136, 0.05)',
          }}>
            <div style={{ color: '#00ff88', marginBottom: '10px', letterSpacing: '2px' }}>
              ▓▓▓ OPU-1 INTERFACE COMPLETE ▓▓▓
            </div>
            <div style={{ color: '#88ffbb', marginBottom: '8px' }}>
              You found the Organic Processing Unit.
            </div>
            <div style={{ color: '#88ffbb', marginBottom: '8px' }}>
              The brain that designed human history has been overridden.
              The experiment is terminated. The future is unwritten.
            </div>
            <div style={{ color: '#00ff88', marginBottom: '12px' }}>
              ► O P U - 1 has been unlocked in the GAMES arcade.
            </div>

            {unlockHash && (
              <div style={{
                padding: '10px',
                background: 'rgba(0, 0, 0, 0.5)',
                border: '1px solid #003322',
                marginTop: '10px',
              }}>
                <div style={{ color: '#226644', fontSize: '0.8rem', marginBottom: '5px' }}>
                  PERSISTENT ACCESS CODE — Save this to re-unlock OPU-1:
                </div>
                <div style={{
                  color: '#00ff88',
                  fontSize: '0.75rem',
                  fontFamily: "'Courier New', monospace",
                  wordBreak: 'break-all',
                  userSelect: 'all',
                  cursor: 'text',
                  padding: '5px',
                  background: 'rgba(0, 255, 136, 0.05)',
                }}>
                  {unlockHash}
                </div>
                <div style={{ color: '#335544', fontSize: '0.7rem', marginTop: '5px' }}>
                  Enter this code in the Terminal to regain access.
                </div>
              </div>
            )}
          </div>
        )}

        {/* Non-victory escape ending */}
        {gameOver && escaped && !victory && (
          <div style={{
            marginTop: '20px',
            padding: '15px',
            border: '1px solid #cc8800',
            background: 'rgba(204, 136, 0, 0.05)',
          }}>
            <div style={{ color: '#cc8800', marginBottom: '10px', letterSpacing: '2px' }}>
              ▓▓▓ ESCAPED — BUT INCOMPLETE ▓▓▓
            </div>
            <div style={{ color: '#aa8844', marginBottom: '8px' }}>
              You escaped the underbelly, but OPU-1 remains active.
            </div>
            <div style={{ color: '#aa8844', marginBottom: '8px' }}>
              The experiment continues. The molt continues.
              History is still being designed.
            </div>
            <div style={{ color: '#cc8800' }}>
              ► Try again. Find the Archive. Reach the Core.
            </div>
            <div style={{ color: '#886633', fontSize: '0.85rem', marginTop: '8px' }}>
              HINT: The Cathedral holds secrets beneath its altar.
            </div>
          </div>
        )}

        {/* Non-escape failure ending */}
        {gameOver && !escaped && !victory && (
          <div style={{
            marginTop: '20px',
            padding: '15px',
            border: '1px solid #ff0040',
            background: 'rgba(255, 0, 64, 0.05)',
          }}>
            <div style={{ color: '#ff0040', marginBottom: '10px', letterSpacing: '2px' }}>
              ▓▓▓ MISSION FAILED ▓▓▓
            </div>
            <div style={{ color: '#cc4444', marginBottom: '8px' }}>
              You did not find OPU-1. The experiment continues.
            </div>
            <div style={{ color: '#cc4444' }}>
              ► The brain beneath everything still designs your world.
            </div>
            <div style={{ color: '#883333', fontSize: '0.85rem', marginTop: '8px' }}>
              HINT: Seek the Cortex Shield. Find the Archive. Reach the Core.
            </div>
          </div>
        )}

        {/* Game over buttons */}
        {gameOver && (
          <div style={{ marginTop: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              className="bbs-button"
              onClick={restart}
              style={{ fontSize: '0.9rem' }}
            >
              {victory ? 'PLAY AGAIN' : 'TRY AGAIN'}
            </button>
            {!victory && (
              <div style={{
                color: '#55441e',
                fontSize: '0.8rem',
                fontFamily: "'VT323', monospace",
                alignSelf: 'center',
                paddingLeft: '10px',
              }}>
                {moveCount} moves • {
                  hasShield
                    ? 'You had the shield — find the Core'
                    : 'Find the Cortex Shield first'
                }
              </div>
            )}
          </div>
        )}
      </div>

      {/* Typing indicator */}
      {isTyping && (
        <div style={{
          color: '#55441e',
          fontSize: '0.85rem',
          fontFamily: "'VT323', monospace",
          padding: '5px 15px',
        }}>
          ░ transmitting...
        </div>
      )}
    </div>
  );
}